type IdValue = {id: string; value: unknown};

/**
 * Used to get new value ids when the order of the values changes,
 * and reuse old id even if value changes.
 *
 * Can change value without updating id, which would cause rerender (and lost focus)
 */
export const getNewValueIds = (
  oldValues: Array<IdValue>,
  values: Array<unknown>,
  getNewId: () => string,
): Array<IdValue> => {
  // First, set exact matches
  const matchStatus = oldValues.map((o, i) => {
    const isExact = o.value === values[i];
    return {
      oldIndex: i,
      id: o.id,
      value: o.value,
      newIndex: isExact ? i : null,
      match: (isExact ? 'exact' : null) as 'exact' | 'moved' | null,
      used: false,
    };
  });

  // Then, set moved value matches
  matchStatus.forEach((status, i) => {
    if (status.match === 'exact') {
      return;
    }

    const movedToIndex = values.findIndex(
      (v, idx) =>
        v === status.value && idx !== i && matchStatus[idx]?.match !== 'exact',
    );

    if (movedToIndex !== -1) {
      status.match = 'moved';
      status.newIndex = movedToIndex;
    }
  });

  /*
    When adding change request item 3, but the original data only has 1 item, an empty key 1 is created.

    arr = {
      0: {item}
      2: {item}
    }
    arr.length = 3

    Js will interpret the array length as 3, even though there is no item key 1 (only 0 and 2).
    We loop and add a dummy item in matchStatus to prevent errors when accessing matchStatus[1].
   */
  for (let i = 0; i < matchStatus.length; i++) {
    if (matchStatus[i] === undefined) {
      matchStatus[i] = {
        oldIndex: i,
        id: 'dummy',
        value: undefined,
        newIndex: null,
        match: null,
        used: false,
      };
    }
  }

  return values.map((value, newIndex) => {
    if (matchStatus[newIndex]?.match === 'exact') {
      matchStatus[newIndex].used = true;
      return oldValues[newIndex];
    }

    const moved = matchStatus.find(
      m => m.newIndex === newIndex && m.match === 'moved',
    );

    if (moved) {
      moved.used = true;
      return oldValues[moved.oldIndex];
    }

    const unused = matchStatus.find(m => !m.used && m.match === null);
    if (unused) {
      unused.used = true;
      return {id: unused.id, value};
    }

    return {id: getNewId(), value};
  });
};
